import { useState, useEffect, useRef } from 'react';
import { CheckCircle, Sparkle, Warning, ArrowClockwise, X, MapPin, FileText, Package, ShoppingCart, MagnifyingGlass, Eye, EyeSlash, Paperclip } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

// ── Product picker (autocomplete) ────────────────────────────────────────────
function ProductPicker({ initial, onSelect, onClose }) {
  const [q, setQ] = useState(initial || '');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const t = setTimeout(async () => {
      if (q.trim().length < 2) { setResults([]); return; }
      setLoading(true);
      try {
        const r = await fetch(`${API}/api/products?q=${encodeURIComponent(q)}&limit=10`, { credentials: 'include' });
        if (r.ok) setResults((await r.json()).products || []);
      } catch { /* ignore */ }
      setLoading(false);
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="absolute z-50 mt-1 left-0 right-0 bg-white border border-zinc-200 rounded-sm shadow-lg max-h-72 overflow-auto" data-testid="product-picker">
      <div className="sticky top-0 bg-white border-b border-zinc-100 p-2">
        <div className="relative">
          <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            ref={inputRef}
            data-testid="product-picker-input"
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Zoek artikel (code of omschrijving)..."
            className="w-full pl-7 pr-7 py-1.5 text-xs border border-zinc-200 rounded-sm focus:outline-none focus:border-[#0F172A]"
          />
          <button
            type="button"
            onClick={onClose}
            className="absolute right-1 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
          ><X size={11} /></button>
        </div>
      </div>
      {loading && <div className="px-3 py-3 text-[11px] text-zinc-400">Zoeken...</div>}
      {!loading && q.trim().length >= 2 && results.length === 0 && (
        <div className="px-3 py-3 text-[11px] text-zinc-400">Geen producten gevonden voor "{q}"</div>
      )}
      {!loading && q.trim().length < 2 && (
        <div className="px-3 py-3 text-[11px] text-zinc-400">Typ minstens 2 tekens om te zoeken</div>
      )}
      {results.map(p => (
        <button
          key={p.code}
          type="button"
          data-testid={`product-option-${p.code}`}
          onClick={() => { onSelect(p); onClose?.(); }}
          className="w-full text-left px-3 py-2 text-xs hover:bg-zinc-50 border-b border-zinc-50 last:border-0"
        >
          <div className="flex items-baseline justify-between gap-2">
            <span className="font-data font-bold text-zinc-800">{p.code}</span>
            {p.unit && <span className="text-[10px] text-zinc-400 uppercase tracking-wider">{p.unit}</span>}
          </div>
          <div className="text-zinc-600 mt-0.5 truncate">{p.description}</div>
          {p.search_code && <div className="text-[10px] text-zinc-400 mt-0.5">{p.search_code}</div>}
        </button>
      ))}
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('nl-NL'); } catch { return iso; }
}
function fmtAddress(a) {
  if (!a) return '—';
  const parts = [a.company, a.street, [a.postcode, a.city].filter(Boolean).join(' '), a.country].filter(Boolean);
  return parts.length ? parts.join(', ') : '—';
}

// ── Order lines: no prices, only article-code + description + qty ───────────

export default function OrderEolPanel({ order: initialOrder, onUpdate }) {
  const [order, setOrder] = useState(initialOrder);
  const [extracting, setExtracting] = useState(false);
  const [showEolForm, setShowEolForm] = useState(false);
  const [eolNumber, setEolNumber] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  // Inline product picker — index of the line we're editing, or null
  const [pickerLine, setPickerLine] = useState(null);
  // Inline manual article-number typing per line: { [idx]: { value, status, msg } }
  // status: 'idle' | 'checking' | 'not_found' | 'error'
  const [manualCode, setManualCode] = useState({});
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackField, setFeedbackField] = useState('delivery_address');
  const [feedbackIssue, setFeedbackIssue] = useState('');
  const [feedbackCorrect, setFeedbackCorrect] = useState('');
  const [feedbackSaving, setFeedbackSaving] = useState(false);

  const submitFeedback = async () => {
    if (!feedbackIssue.trim()) return;
    setFeedbackSaving(true);
    try {
      const r = await fetch(`${API}/api/orders/${order.order_id}/extraction-feedback`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ field: feedbackField, issue: feedbackIssue, correct_value: feedbackCorrect }),
      });
      if (r.ok) {
        setFeedbackOpen(false);
        setFeedbackIssue(''); setFeedbackCorrect('');
        // eslint-disable-next-line no-alert
        window.alert('Bedankt — het systeem leert hier van bij toekomstige orders van dezelfde klant.');
      } else {
        const err = await r.json().catch(() => ({}));
        window.alert(err.detail || 'Opslaan mislukt');
      }
    } catch { window.alert('Verbindingsfout'); }
    setFeedbackSaving(false);
  };

  const isProcessed = !!order.eol_processed;

  const refresh = (next) => {
    setOrder(next);
    onUpdate?.(next);
  };

  // Replace a line with a catalog product (no prices — only code + description + unit)
  const applyCatalogProduct = async (lineIdx, product) => {
    const items = (order.items || []).map((it, i) => {
      if (i !== lineIdx) return it;
      return {
        ...it,
        article_number: product.code,
        article_name: product.description,
        unit: product.unit || it.unit || 'Stuk',
        match_confidence: 'high',
      };
    });
    const next = { ...order, items };
    setOrder(next);
    setPickerLine(null);
    // Persist (only items — prices are not used for EOL push)
    try {
      const r = await fetch(`${API}/api/orders/${order.order_id}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!r.ok) setError('Opslaan mislukt');
    } catch { setError('Verbindingsfout'); }
    onUpdate?.(next);
  };

  // Admin typt handmatig een artikelcode in het lege Artikelnr-veld.
  // We valideren tegen de catalogus: bij match → gebruik applyCatalogProduct
  // (dat triggert ook de mapping-leerflow via PUT /orders). Bij no-match tonen we
  // een waarschuwing zodat de admin de code kan corrigeren.
  const submitManualCode = async (lineIdx, rawCode) => {
    const code = (rawCode || '').trim();
    if (!code) return;
    setManualCode(m => ({ ...m, [lineIdx]: { ...(m[lineIdx] || {}), value: code, status: 'checking', msg: '' } }));
    try {
      const r = await fetch(`${API}/api/products?q=${encodeURIComponent(code)}&limit=5`, { credentials: 'include' });
      if (!r.ok) throw new Error('search_failed');
      const data = await r.json();
      const products = data.products || [];
      const exact = products.find(p => (p.code || '').toLowerCase() === code.toLowerCase());
      if (exact) {
        await applyCatalogProduct(lineIdx, exact);
        setManualCode(m => { const n = { ...m }; delete n[lineIdx]; return n; });
        return;
      }
      setManualCode(m => ({
        ...m,
        [lineIdx]: {
          value: code,
          status: 'not_found',
          msg: products.length > 0
            ? `Geen exacte match. Bedoelde je: ${products.slice(0, 3).map(p => p.code).join(', ')}?`
            : 'Artikelcode bestaat niet in de catalogus',
        },
      }));
    } catch {
      setManualCode(m => ({ ...m, [lineIdx]: { value: code, status: 'error', msg: 'Verbindingsfout bij valideren' } }));
    }
  };

  const runExtract = async () => {
    setExtracting(true);
    setError('');
    try {
      const r = await fetch(`${API}/api/orders/${order.order_id}/extract-from-email`, {
        method: 'POST', credentials: 'include',
      });
      const data = await r.json().catch(() => ({}));
      if (r.ok) refresh(data);
      else setError(data.detail || 'Extractie mislukt');
    } catch {
      setError('Verbindingsfout');
    }
    setExtracting(false);
  };

  const markProcessed = async () => {
    if (!eolNumber.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      const r = await fetch(`${API}/api/orders/${order.order_id}/mark-eol-processed`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ eol_order_number: eolNumber.trim() }),
      });
      const data = await r.json().catch(() => ({}));
      if (r.ok) {
        refresh(data);
        setShowEolForm(false);
        setEolNumber('');
      } else {
        setError(data.detail || 'Markeren mislukt');
      }
    } catch {
      setError('Verbindingsfout');
    }
    setSubmitting(false);
  };

  const unmarkProcessed = async () => {
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/api/orders/${order.order_id}/unmark-eol-processed`, {
        method: 'POST', credentials: 'include',
      });
      const data = await r.json().catch(() => ({}));
      if (r.ok) refresh(data);
    } catch { /* ignore */ }
    setSubmitting(false);
  };

  const items = order.items || [];
  const noStructuredData = !order.last_extracted_at && !order.customer_code && items.length === 0;

  return (
    <div
      className={`border rounded-sm p-4 mb-4 ${isProcessed ? 'border-emerald-300 bg-emerald-50/40' : 'border-purple-200 bg-purple-50/30'}`}
      data-testid={`order-eol-panel-${order.order_id}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <ShoppingCart size={18} weight="duotone" className={isProcessed ? 'text-emerald-700' : 'text-purple-700'} />
          <span className="font-heading text-sm font-bold text-[#0F172A]">Order overzicht</span>
          <span className="text-[11px] font-data text-zinc-500">{order.reference || order.order_id}</span>
          {isProcessed && (
            <span
              data-testid={`eol-processed-badge-${order.order_id}`}
              className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider font-medium rounded-sm bg-emerald-600 text-white"
            >
              <CheckCircle size={10} weight="fill" /> Verwerkt in EOL #{order.eol_order_number}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Bijlage hint zodat user ziet dat er PDFs worden meegelezen */}
          {Array.isArray(order.incoming_attachments_summary) && order.incoming_attachments_summary.length > 0 && (
            <span
              data-testid={`pdf-hint-${order.order_id}`}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-bold rounded-sm bg-indigo-100 text-indigo-700 border border-indigo-200"
              title={`${order.incoming_attachments_summary.length} bijlage(n) zullen meegelezen worden door de AI`}
            >
              <Paperclip size={9} weight="fill" /> {order.incoming_attachments_summary.length} pdf
            </span>
          )}
          {typeof order.last_extracted_pdfs === 'number' && order.last_extracted_pdfs > 0 && (
            <span
              className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-bold rounded-sm bg-emerald-100 text-emerald-700 border border-emerald-200"
              title="Aantal PDFs dat de AI heeft gelezen tijdens de laatste extractie"
            >
              ✓ {order.last_extracted_pdfs} pdf gelezen
            </span>
          )}
          <button
            type="button"
            data-testid={`extract-order-btn-${order.order_id}`}
            onClick={runExtract}
            disabled={extracting || isProcessed}
            title="Lees de inkomende email én eventueel bijgevoegde PDF's met AI en vul automatisch de order-regels"
            className="px-2.5 py-1.5 text-[11px] border border-zinc-200 text-zinc-700 rounded-sm hover:bg-zinc-50 disabled:opacity-40 transition-colors flex items-center gap-1"
          >
            {extracting
              ? <><ArrowClockwise size={11} className="animate-spin" /> AI bezig...</>
              : <><Sparkle size={11} weight="fill" /> {order.last_extracted_at ? 'Opnieuw analyseren' : 'AI-analyse (incl. PDF)'}</>}
          </button>
          {order.last_extracted_at && !isProcessed && (
            <button
              type="button"
              onClick={() => setFeedbackOpen(o => !o)}
              data-testid={`extraction-feedback-btn-${order.order_id}`}
              title="Meld een verkeerd geëxtraheerd veld zodat de AI volgende keer beter leest voor deze klant"
              className="px-2 py-1.5 text-[11px] border border-zinc-200 text-zinc-700 rounded-sm hover:bg-amber-50 hover:border-amber-300 hover:text-amber-700 transition-colors flex items-center gap-1"
            >
              🚩 Fout?
            </button>
          )}
        </div>
      </div>

      {/* Feedback panel */}
      {feedbackOpen && (
        <div className="mb-3 border border-amber-200 bg-amber-50/40 rounded-sm p-3" data-testid="extraction-feedback-panel">
          <div className="text-xs font-semibold text-amber-800 uppercase tracking-wider mb-2 flex items-center gap-1">
            🚩 Meld verkeerd geëxtraheerd veld
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
            <select
              value={feedbackField}
              onChange={e => setFeedbackField(e.target.value)}
              data-testid="feedback-field-select"
              className="px-2 py-1.5 text-xs border border-zinc-200 rounded-sm bg-white"
            >
              <option value="delivery_address">Afleveradres</option>
              <option value="invoice_address">Factuuradres</option>
              <option value="delivery_date">Leverdatum</option>
              <option value="customer_reference">Uw referentie</option>
              <option value="items">Orderregels</option>
              <option value="other">Overig</option>
            </select>
            <input
              value={feedbackIssue}
              onChange={e => setFeedbackIssue(e.target.value)}
              placeholder="Wat ging er fout?"
              data-testid="feedback-issue-input"
              className="px-2 py-1.5 text-xs border border-zinc-200 rounded-sm md:col-span-2"
            />
          </div>
          <input
            value={feedbackCorrect}
            onChange={e => setFeedbackCorrect(e.target.value)}
            placeholder="Wat was de juiste waarde? (optioneel)"
            data-testid="feedback-correct-input"
            className="w-full px-2 py-1.5 text-xs border border-zinc-200 rounded-sm mb-2"
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={submitFeedback}
              disabled={!feedbackIssue.trim() || feedbackSaving}
              data-testid="feedback-submit-btn"
              className="px-3 py-1.5 text-xs bg-amber-600 text-white rounded-sm hover:bg-amber-700 disabled:opacity-50 transition-colors"
            >
              {feedbackSaving ? 'Versturen...' : 'Verstuur feedback'}
            </button>
            <button
              type="button"
              onClick={() => setFeedbackOpen(false)}
              className="px-3 py-1.5 text-xs border border-zinc-200 rounded-sm hover:bg-zinc-50"
            >
              Annuleer
            </button>
            <span className="text-[11px] text-amber-700/80">Wordt automatisch toegepast bij toekomstige orders van deze klant.</span>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-3 px-3 py-2 text-xs bg-red-50 border border-red-200 text-red-800 rounded-sm flex items-center gap-2">
          <Warning size={12} weight="fill" /> {error}
        </div>
      )}

      {noStructuredData && !extracting && (
        <div className="mb-3 px-3 py-2 text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-sm">
          Klik op <strong>AI-analyse</strong> om de email + bijlagen te ontleden tot een gestructureerde Exact Online order.
        </div>
      )}

      {/* Top fields grid (3 columns like EOL) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
        <FieldBlock label="Besteld door" icon={MapPin}>
          <div className="text-zinc-800 font-medium">{order.customer?.company || order.customer?.name || '—'}</div>
          {order.customer_code && <div className="text-[11px] text-zinc-500">Klantcode: <span className="font-data">{order.customer_code}</span></div>}
          {order.contact_name && <div className="text-[11px] text-zinc-500">t.a.v. {order.contact_name}</div>}
          {order.customer?.email && <div className="text-[11px] text-zinc-500 truncate">{order.customer.email}</div>}
        </FieldBlock>
        <AddressFieldBlock
          label="Leveren aan"
          icon={Package}
          address={order.delivery_address}
          raw={order.delivery_address_raw}
          testId={`delivery-address-${order.order_id}`}
        />
        <AddressFieldBlock
          label="Factuur voor"
          icon={FileText}
          address={order.invoice_address || order.delivery_address}
          raw={order.invoice_address_raw || (order.invoice_address ? null : order.delivery_address_raw)}
          testId={`invoice-address-${order.order_id}`}
        />
      </div>

      {/* Meta row: orderdatum, leveringswijze, uw ref. */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4 text-xs">
        <MetaCell label="Orderdatum" value={fmtDate(order.created_at)} />
        <MetaCell label="Leveringswijze" value={order.delivery_method || 'DAP'} />
        <MetaCell label="Uw ref." value={order.customer_reference || '—'} />
      </div>

      {order.comments && (
        <div className="mb-3 px-3 py-2 text-xs bg-white border border-zinc-200 rounded-sm">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Omschrijving</div>
          <div className="text-zinc-700 italic">{order.comments}</div>
        </div>
      )}

      {/* Order lines table — geen prijzen, enkel artikel/aantal voor EOL push */}
      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden mb-3">
        <table className="w-full text-xs" data-testid={`order-lines-${order.order_id}`}>
          <thead>
            <tr className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500">
              <th className="px-3 py-2 text-left">Artikelnr</th>
              <th className="px-3 py-2 text-left">Omschrijving</th>
              <th className="px-3 py-2 text-right">Aantal</th>
              <th className="px-3 py-2 text-left">Eenheid</th>
              <th className="px-3 py-2 text-right w-10"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-zinc-400">Geen orderregels — klik op AI-analyse</td></tr>
            ) : (
              items.map((it, i) => {
                const unmatched = !it.article_number || it.match_confidence === 'low';
                return (
                <tr
                  key={i}
                  data-testid={`order-line-${i}`}
                  className={`border-t border-zinc-100 relative ${unmatched ? 'bg-yellow-50' : ''}`}
                >
                  <td className="px-3 py-2 font-data align-top relative">
                    {it.article_number ? (
                      <span className="text-zinc-800 font-medium">{it.article_number}</span>
                    ) : !isProcessed ? (
                      <div className="flex flex-col gap-1">
                        <input
                          data-testid={`manual-code-input-${i}`}
                          type="text"
                          defaultValue={manualCode[i]?.value || ''}
                          placeholder="Typ artikelnr..."
                          onChange={() => {
                            // Wis error-state zodra gebruiker opnieuw typt
                            if (manualCode[i]?.status === 'not_found' || manualCode[i]?.status === 'error') {
                              setManualCode(m => { const n = { ...m }; delete n[i]; return n; });
                            }
                          }}
                          onBlur={(e) => {
                            const v = e.target.value.trim();
                            if (v) submitManualCode(i, v);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') { e.preventDefault(); submitManualCode(i, e.target.value); }
                            if (e.key === 'Escape') e.target.blur();
                          }}
                          className={`w-32 px-1.5 py-1 text-xs font-data border rounded-sm focus:outline-none ${
                            manualCode[i]?.status === 'not_found' || manualCode[i]?.status === 'error'
                              ? 'border-red-400 bg-red-50 focus:border-red-500'
                              : 'border-yellow-400 bg-white focus:border-[#0F172A]'
                          }`}
                        />
                        {manualCode[i]?.status === 'checking' && (
                          <span className="text-[9px] text-zinc-500">Controleren...</span>
                        )}
                        {(manualCode[i]?.status === 'not_found' || manualCode[i]?.status === 'error') && (
                          <span data-testid={`manual-code-err-${i}`} className="text-[9px] text-red-600 leading-tight">{manualCode[i].msg}</span>
                        )}
                        {!manualCode[i]?.status && (
                          <span className="text-[9px] text-yellow-800 uppercase tracking-wider font-semibold">Geen match</span>
                        )}
                      </div>
                    ) : (
                      <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-bold rounded-sm bg-yellow-200 text-yellow-900 border border-yellow-300"
                      >
                        <Warning size={9} weight="fill" /> Geen match
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 align-top">
                    <div className="font-medium text-zinc-800">
                      {it.article_name || it.description || '—'}
                    </div>
                    {it.customer_article_code && (
                      <div className="text-[10px] text-zinc-400 mt-0.5 font-data">
                        Klantcode: {it.customer_article_code}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right font-data align-top font-semibold">{it.quantity}</td>
                  <td className="px-3 py-2 text-zinc-600 align-top">{it.unit || 'stuk'}</td>
                  <td className="px-3 py-2 align-top text-right relative">
                    {!isProcessed && (
                      <button
                        type="button"
                        data-testid={`pick-product-btn-${i}`}
                        onClick={() => setPickerLine(pickerLine === i ? null : i)}
                        title={it.article_number ? "Wijzig artikel" : "Koppel artikel uit catalogus"}
                        className={`px-1.5 py-1 text-[10px] border rounded-sm transition-colors flex-shrink-0 ${
                          unmatched
                            ? 'border-yellow-400 bg-yellow-100 text-yellow-900 hover:bg-yellow-200'
                            : 'border-zinc-200 text-zinc-600 hover:bg-blue-50 hover:border-blue-200 hover:text-blue-700'
                        }`}
                      >
                        <MagnifyingGlass size={11} />
                      </button>
                    )}
                    {pickerLine === i && (
                      <ProductPicker
                        initial={(it.article_name || it.description || '').split(' ').slice(0, 2).join(' ')}
                        onSelect={(p) => applyCatalogProduct(i, p)}
                        onClose={() => setPickerLine(null)}
                      />
                    )}
                  </td>
                </tr>
              );})
            )}
          </tbody>
        </table>
      </div>

      {/* Hint over ontbrekende artikelen */}
      {items.some(it => !it.article_number || it.match_confidence === 'low') && (
        <div className="mb-3 px-3 py-2 text-[11px] bg-yellow-50 border border-yellow-300 text-yellow-900 rounded-sm flex items-center gap-2" data-testid="unmatched-hint">
          <Warning size={12} weight="fill" />
          <span>Eén of meer regels hebben geen catalogus-match. Typ het juiste EOL-artikelnr rechtstreeks in het veld of klik op het zoek-icoon. Het systeem leert van elke handmatige koppeling voor volgende orders van deze klant.</span>
        </div>
      )}

      {/* EOL action area */}
      {isProcessed ? (
        <div className="flex items-center justify-between gap-3 p-3 border border-emerald-300 bg-emerald-50 rounded-sm">
          <div className="flex items-center gap-2 text-xs text-emerald-900">
            <CheckCircle size={16} weight="fill" className="text-emerald-600" />
            <div>
              <strong>Verwerkt in Exact Online — order #{order.eol_order_number}</strong>
              <div className="text-[11px] text-emerald-700/80">
                op {fmtDate(order.eol_processed_at)} door {order.eol_processed_by}
              </div>
            </div>
          </div>
          <button
            type="button"
            data-testid={`unmark-eol-btn-${order.order_id}`}
            onClick={unmarkProcessed}
            disabled={submitting}
            className="px-3 py-1.5 text-[11px] border border-emerald-300 text-emerald-700 rounded-sm hover:bg-white transition-colors disabled:opacity-40"
          >
            Markering ongedaan maken
          </button>
        </div>
      ) : !showEolForm ? (
        <button
          type="button"
          data-testid={`open-eol-btn-${order.order_id}`}
          onClick={() => { setShowEolForm(true); setError(''); }}
          className="w-full px-4 py-2.5 text-sm bg-emerald-600 text-white rounded-sm hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2 font-medium"
        >
          <CheckCircle size={16} weight="fill" /> Verwerkt in Exact Online
        </button>
      ) : (
        <div className="flex items-center gap-2 p-3 bg-white border border-emerald-300 rounded-sm">
          <span className="text-xs text-zinc-700 whitespace-nowrap">EOL ordernummer:</span>
          <input
            data-testid={`eol-number-input-${order.order_id}`}
            type="text"
            value={eolNumber}
            onChange={e => setEolNumber(e.target.value)}
            placeholder="bv. 11883"
            autoFocus
            className="flex-1 px-3 py-1.5 text-sm font-data border border-zinc-200 rounded-sm focus:outline-none focus:border-emerald-500"
            onKeyDown={e => { if (e.key === 'Enter' && eolNumber.trim()) markProcessed(); }}
          />
          <button
            type="button"
            data-testid={`confirm-eol-btn-${order.order_id}`}
            onClick={markProcessed}
            disabled={submitting || !eolNumber.trim()}
            className="px-3 py-1.5 text-xs bg-emerald-600 text-white rounded-sm hover:bg-emerald-700 disabled:opacity-40 transition-colors flex items-center gap-1"
          >
            <CheckCircle size={11} weight="fill" /> Bevestig
          </button>
          <button
            type="button"
            data-testid={`cancel-eol-btn-${order.order_id}`}
            onClick={() => { setShowEolForm(false); setEolNumber(''); setError(''); }}
            className="px-2 py-1.5 text-xs text-zinc-500 hover:text-zinc-800 transition-colors"
          >
            <X size={12} />
          </button>
        </div>
      )}
    </div>
  );
}

function FieldBlock({ label, icon: Icon, children }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-3">
      <div className="flex items-center gap-1 mb-1.5">
        {Icon && <Icon size={11} className="text-zinc-400" />}
        <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{label}</span>
      </div>
      <div className="text-xs space-y-0.5">{children}</div>
    </div>
  );
}

// Address block with "Toon origineel uit email" toggle so de gebruiker kan
// controleren of de AI het volledige adres uit de mail heeft overgenomen.
function AddressFieldBlock({ label, icon: Icon, address, raw, testId }) {
  const [showRaw, setShowRaw] = useState(false);
  const rawText = (raw || '').trim();
  const hasRaw = rawText.length > 0;
  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-3" data-testid={testId}>
      <div className="flex items-center justify-between gap-1 mb-1.5">
        <div className="flex items-center gap-1">
          {Icon && <Icon size={11} className="text-zinc-400" />}
          <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{label}</span>
        </div>
        {hasRaw && (
          <button
            type="button"
            data-testid={`${testId}-toggle-raw`}
            onClick={() => setShowRaw(s => !s)}
            title={showRaw ? 'Toon AI-geëxtraheerd adres' : 'Toon origineel adres uit email'}
            className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-wider rounded-sm border border-zinc-200 text-zinc-500 hover:bg-indigo-50 hover:border-indigo-200 hover:text-indigo-700 transition-colors"
          >
            {showRaw ? <EyeSlash size={10} /> : <Eye size={10} />}
            {showRaw ? 'AI' : 'Origineel'}
          </button>
        )}
      </div>
      <div className="text-xs space-y-0.5">
        {showRaw ? (
          <pre
            data-testid={`${testId}-raw`}
            className="whitespace-pre-wrap font-data text-[11px] leading-snug text-indigo-900 bg-indigo-50/60 border border-indigo-100 rounded-sm px-2 py-1.5"
          >{rawText}</pre>
        ) : (
          <div className="text-zinc-800">{fmtAddress(address)}</div>
        )}
      </div>
    </div>
  );
}

function MetaCell({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-0.5">{label}</div>
      <div className="text-zinc-800 font-medium">{value || '—'}</div>
    </div>
  );
}
